import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ProductClient, ProductKitClient } from '@frontend/product/api';
import { IProduct, ProductExtended, ProductKitQueryParams } from '@frontend/product/types';
import React, { useRef, useState } from 'react';

import { useAppDispatch } from '../../../../redux/store';
import { LinkKitItemsModal } from './link-kit-items-modal/link-kit-items-modal.component';

export interface KitItemsDetailProps {
    product: ProductExtended;
}

export const useKitItemsDetail = (props: KitItemsDetailProps) => {
    const dispatch = useAppDispatch();
    const { openModal, closeModal } = useModals();
    const [selectedObjects, setSelectedObjects] = useState<IProduct[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [listRefreshKey, setListRefreshKey] = useState<number>(0);
    const quantitiesRef = useRef<Record<string, number>>({});
    const entryIdByProductId = useRef<Map<string, string>>(new Map());
    const unlinkModalId = `kit-items-unlink-modal-${props.product.id}`;
    const linkModalId = `kit-items-link-modal-${props.product.id}`;

    const fetchKitItems = async (arg?: ApiQueryParams<DefaultQueryParams>): Promise<PaginatedResponse<IProduct>> => {
        const entriesResponse = await ProductKitClient.fetchProductKits({
            ...arg,
            kit_id: props.product.id
        } as ApiQueryParams<ProductKitQueryParams>);

        if (entriesResponse.results.length === 0) {
            entryIdByProductId.current = new Map();
            quantitiesRef.current = {};
            return { count: entriesResponse.count, results: [] };
        }

        const productIds = [...new Set(entriesResponse.results.map((e) => e.product_id))];
        const productsResponse = await ProductClient.resolveProducts(productIds);
        const productById = new Map(productsResponse.results.map((p) => [p.id, p]));

        entryIdByProductId.current = new Map(entriesResponse.results.map((e) => [e.product_id, e.id]));
        const nextQuantities: Record<string, number> = {};
        entriesResponse.results.forEach((e) => {
            nextQuantities[e.product_id] = e.quantity;
        });
        quantitiesRef.current = nextQuantities;

        const results = entriesResponse.results.map((e) => productById.get(e.product_id)).filter((p): p is IProduct => p !== undefined);

        return { count: entriesResponse.count, results };
    };

    const refreshList = () => setListRefreshKey((prev) => prev + 1);

    const getQuantity = (productId: string): number => quantitiesRef.current[productId] ?? 1;

    const openLinkModal = () => {
        openModal(
            linkModalId,
            () =>
                React.createElement(LinkKitItemsModal, {
                    modalId: linkModalId,
                    product: props.product,
                    onLinked: refreshList
                }),
            {},
            { className: 'min-w-[6/10]' }
        );
    };

    const unlinkSelectedProducts = async () => {
        if (isSubmitting || selectedObjects.length === 0) return;
        setIsSubmitting(true);

        try {
            const entryIds = selectedObjects.map((product) => entryIdByProductId.current.get(product.id)).filter((id): id is string => id !== undefined);
            if (entryIds.length > 0) await ProductKitClient.deleteProductKits(entryIds);
            setSelectedObjects([]);
            closeModal(unlinkModalId);
            refreshList();
        } catch (e: any) {
            ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        fetchKitItems,
        dispatch,
        selectedObjects,
        setSelectedObjects,
        isSubmitting,
        openLinkModal,
        unlinkSelectedProducts,
        listRefreshKey,
        unlinkModalId,
        openModal,
        closeModal,
        getQuantity
    };
};
